import { z } from 'zod';
import {
  itemStatuses,
  partTypes,
  subscriptionFeatures,
  subscriptionTiers,
  transferReasons,
} from './inventory';
import {
  fulfillmentStates,
  handoverMethods,
  overrideReasons,
  requirementTypes,
  stockUnits,
  stockUsages,
} from './fulfillment';
import { discrepancyKinds, receiptSources, receiptStates } from './goods-intake';
import { shipmentStates } from './shipment';
import { siteNoteKinds } from './siteNote';
import { contactLevels, contactPurposes } from './contact';
import {
  deviceMessages,
  deviceSituations,
  deliveries,
  emailStatuses,
  noticeDecisions,
} from './notification';
import { movementReasons } from './stock';

export const itemSchema = z
  .object({
    id: z.string().trim().min(1, 'Item ID is required'),
    partId: z.string().min(1, 'Part is required'),
    siteId: z.string().min(1, 'Assigned site is required'),
    locationSiteId: z.string().optional(),
    location: z.string().max(500).optional(),
    description: z.string().max(500).optional(),
    identity: z.string().max(500).optional(),
    status: z.enum(itemStatuses).optional(),
    notes: z.string().max(5000).optional(),
    lastTransfer: z
      .object({
        trackingNumber: z.string().max(200).optional(),
        reason: z.enum(transferReasons).optional(),
        transferTime: z.string().datetime().optional(),
      })
      .optional(),
    inspectedDate: z.string().datetime().optional(),
    inspectedBy: z.string().max(320).optional(),
    maintenanceDate: z.string().datetime().optional(),
    maintenanceBy: z.string().max(320).optional(),
    maintenanceNotes: z.string().max(5000).optional(),
    shippingDate: z.string().datetime().optional(),
    installationDate: z.string().datetime().optional(),
    decommissionDate: z.string().datetime().optional(),
    needsAttention: z.boolean().optional(),
    attentionReason: z.string().max(1000).optional(),
    hardwareRevision: z.string().max(100).optional(),
    replacedByItemId: z.string().max(200).optional(),
    replacedAt: z.string().datetime().optional(),
    version: z.coerce.number().int().nonnegative().default(0),
    lastModifiedBy: z.string().optional(),
    lastModified: z.string().datetime().optional(),
  })
  .superRefine((item, context) => {
    if (item.needsAttention && !item.attentionReason?.trim()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['attentionReason'],
        message: 'Attention reason is required when attention is enabled',
      });
    }
    if (!item.needsAttention && item.attentionReason?.trim()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['attentionReason'],
        message: 'Clear the attention reason when attention is disabled',
      });
    }
  });

export const itemUsageSchema = z.object({
  id: z.string().min(1),
  lastUsed: z.string().date().optional(),
  lastSiteId: z.string().optional(),
  lastSiteName: z.string().optional(),
  lastPen: z.string().optional(),
  daysUsed: z.coerce.number().int().nonnegative().optional(),
});

export const partSchema = z.object({
  id: z.string().min(1),
  type: z.enum(partTypes),
  build: z.string(),
  version: z.coerce.number().int().nonnegative().default(0),
  lastModifiedBy: z.string().optional(),
  lastModified: z.string().optional(),
});

export const customerSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  displayName: z.string().optional(),
  customerType: z.string().optional(),
  state: z.string(),
  labels: z.record(z.string(), z.string()).optional(),
  version: z.coerce.number().int().nonnegative().default(0),
  lastModifiedBy: z.string().optional(),
  lastModified: z.string().optional(),
});

export const siteAddressSchema = z.object({
  line1: z.string().optional(),
  line2: z.string().optional(),
  postcode: z.string().optional(),
  city: z.string().optional(),
});

export const siteSchema = z.object({
  id: z.string().min(1),
  customerId: z.string(),
  name: z.string(),
  displayName: z.string().optional(),
  region: z.string().optional(),
  officialSiteId: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  labels: z.record(z.string(), z.string()).optional(),
  orderedSystems: z.coerce.number().int().nonnegative().optional(),
  address: siteAddressSchema.optional(),
  version: z.coerce.number().int().nonnegative().default(0),
  lastModifiedBy: z.string().optional(),
  lastModified: z.string().optional(),
});

export const unitSchema = z.object({
  id: z.string().min(1),
  siteId: z.string().min(1),
  displayName: z.string().optional(),
  status: z.string().optional(),
  waterQualityDelegateUnitName: z.string().optional(),
  version: z.coerce.number().int().nonnegative().default(0),
  lastModifiedBy: z.string().optional(),
  lastModified: z.string().optional(),
});

// The wire shape is `features: [{ name: "BIOMASS" }, ...]` (repeated Feature
// message), not a plain string array — flatten it here rather than in every
// call site. FEATURE_UNSPECIFIED entries are dropped as data noise, matching
// how STATUS_UNSPECIFIED is treated as a defect rather than a real value.
export const subscriptionSchema = z
  .object({
    id: z.string().min(1),
    customerId: z.string(),
    tier: z.enum(subscriptionTiers).optional(),
    features: z
      .array(z.object({ name: z.enum(subscriptionFeatures).optional() }))
      .optional()
      .default([])
      .transform((entries) =>
        entries
          .map((entry) => entry.name)
          .filter(
            (name): name is Exclude<(typeof subscriptionFeatures)[number], 'FEATURE_UNSPECIFIED'> =>
              Boolean(name) && name !== 'FEATURE_UNSPECIFIED',
          ),
      ),
    contractName: z.string().optional(),
    contractUrl: z.string().optional(),
    notes: z.string().max(5000).optional(),
    numberOfSystems: z.coerce.number().int().nonnegative().optional(),
    regions: z.array(z.string()).optional(),
    startTime: z.string().optional(),
    endTime: z.string().optional(),
    updateTime: z.string().optional(),
    revision: z.coerce.number().int().nonnegative().default(0),
  })
  .transform((subscription) => ({
    ...subscription,
    features: [...new Set(subscription.features)],
  }));

const serialAssignmentSchema = z.object({
  itemId: z.string().min(1),
  source: z.enum(['HEARTBEAT_INFERENCE', 'FIELD_ENTRY']),
  confidence: z.enum(['UNCONFIRMED']).optional(),
  note: z.string().optional(),
});

const consumableLineSchema = z.object({
  description: z.string(),
  quantity: z.coerce.number(),
  unit: z.enum(['METERS', 'PIECES', 'KILOGRAMS']),
  partId: z.string().optional(),
});

const shipmentPalletSchema = z.object({
  id: z.string().min(1),
  cargonizerPieceNumber: z.string(),
  // Optional on the wire: a pallet booked with only a weight has no
  // dimensions, and coercing a missing value would give NaN.
  weightKg: z.coerce.number().default(0),
  lengthCm: z.coerce.number().default(0),
  widthCm: z.coerce.number().default(0),
  heightCm: z.coerce.number().default(0),
  serials: z.array(serialAssignmentSchema).optional().default([]),
  consumables: z.array(consumableLineSchema).optional().default([]),
});

const deliveryStatusSchema = z.object({
  status: z.string(),
  deliveredAt: z.string(),
  source: z.enum(['BRING_TRACKING_API']),
});

export const shipmentAddressSchema = z.object({
  line1: z.string(),
  line2: z.string().optional(),
  postcode: z.string(),
  city: z.string(),
});

export const cargonizerCustomerSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  address: shipmentAddressSchema,
  contactPerson: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
});

export const shipmentEndpointSchema = z.object({
  customerId: z.string().optional(),
  siteId: z.string().optional(),
  name: z.string().catch(''),
  contactName: z.string().optional(),
  contactPhone: z.string().optional(),
  address: shipmentAddressSchema.optional(),
});

export const shipmentEvidenceSchema = z.object({
  id: z.string().min(1),
  source: z
    .enum(['CARGONIZER', 'BRING_TRACKING_API', 'SOURCE_UNSPECIFIED'])
    .catch('SOURCE_UNSPECIFIED'),
  confirmation: z
    .enum(['CONFIRMED', 'INFERRED', 'CONFIRMATION_UNSPECIFIED'])
    .catch('CONFIRMATION_UNSPECIFIED'),
  kind: z.string().catch(''),
  description: z.string().optional(),
  eventTime: z.string().optional(),
  eventTimeApproximate: z.boolean().optional().default(false),
  observedAt: z.string().optional(),
  location: z.string().optional(),
  reference: z.string().optional(),
  recordedBy: z.string().optional(),
});

export const trackingRefreshResultSchema = z.object({
  shipmentsChecked: z.coerce.number().int().default(0),
  evidenceRecorded: z.coerce.number().int().default(0),
  deliveriesRecorded: z.coerce.number().int().default(0),
  bringEnabled: z.boolean().optional().default(false),
  errors: z.array(z.string()).optional().default([]),
});

export const shipmentSchema = z.object({
  id: z.string().min(1),
  // Shipments stored before directions existed have none; they're outbound.
  direction: z.enum(['OUTBOUND', 'RETURN']).catch('OUTBOUND').default('OUTBOUND'),
  origin: shipmentEndpointSchema.optional(),
  destinationSiteId: z.string().optional(),
  destinationCustomerId: z.string().optional(),
  destinationContactName: z.string().optional(),
  destinationContactPhone: z.string().optional(),
  cargonizerConsignmentId: z.string().optional(),
  cargonizerConsignmentNumber: z.string(),
  carrier: z.string(),
  destinationCustomer: z.string(),
  destinationAddress: shipmentAddressSchema,
  shippedAt: z.string(),
  state: z.enum(shipmentStates),
  cancelNote: z.string().optional(),
  trackingUrl: z.string().optional(),
  delivery: deliveryStatusSchema.optional(),
  pallets: z.array(shipmentPalletSchema).optional().default([]),
  unassignedSerials: z.array(serialAssignmentSchema).optional().default([]),
  reason: z.string().optional(),
  senderReference: z.string().optional(),
  version: z.coerce.number().int().nonnegative().default(0),
  lastModifiedBy: z.string().optional(),
  lastModified: z.string().optional(),
  requestedBy: z.string().optional(),
});

const fieldWorkLogisticsSchema = z.object({
  // `.catch` (not `.default`) so a legacy/unknown wire value falls back
  // instead of throwing -- the field_work_requests table was repurposed
  // in place and may still hold pre-migration rows (see ADR-0008).
  mode: z.enum(['NONE', 'CARGONIZER', 'HAND_CARRY', 'OTHER']).catch('NONE'),
  shipmentId: z.string().optional(),
  notes: z.string().optional(),
});

export const fieldWorkRequestSchema = z.object({
  id: z.string().min(1),
  title: z.string().catch(''),
  description: z.string().catch(''),
  source: z
    .enum(['CUSTOMER_SUCCESS', 'SALES', 'ENGINEERING', 'REMOTE_OPS', 'AD_HOC'])
    .optional()
    .catch(undefined),
  customerId: z.string().optional(),
  siteId: z.string().optional(),
  relatedItemIds: z.array(z.string()).catch(() => []),
  githubIssueUrl: z.string().optional(),
  team: z.enum(['EUROPE', 'CHILE']).catch('EUROPE'),
  priority: z.enum(['URGENT', 'HIGH', 'NORMAL', 'LOW']).optional().catch(undefined),
  queuePosition: z.coerce.number().int().nonnegative().catch(0),
  assignee: z.string().catch(''),
  dueDate: z.string().optional(),
  status: z.enum(['NEW', 'TRIAGED', 'IN_PROGRESS', 'BLOCKED', 'COMPLETE', 'REJECTED']).catch('NEW'),
  blocker: z.string().optional(),
  logistics: fieldWorkLogisticsSchema.optional(),
  completionNotes: z.string().optional(),
  requestedBy: z.string().optional(),
  requestedAt: z.string().catch(''),
  triagedAt: z.string().optional(),
  startedAt: z.string().optional(),
  completedAt: z.string().optional(),
  blockedTotalSeconds: z.coerce.number().int().nonnegative().catch(0),
  blockedSince: z.string().optional(),
  version: z.coerce.number().int().nonnegative().catch(0),
  lastModified: z.string().optional(),
  lastModifiedBy: z.string().optional(),
  // Factory, not a literal: each request needs its own array.
  activity: z
    .array(
      z.object({
        id: z.string().catch(''),
        kind: z.enum(['NOTE', 'HANDOFF', 'ASSIGNED']),
        note: z.string().optional(),
        fromAssignee: z.string().optional(),
        toAssignee: z.string().optional(),
        author: z.string().optional(),
        createTime: z.string().optional(),
      }),
    )
    .catch(() => []),
});

export const fieldOpsQueueOwnerSchema = z.object({
  id: z.string().min(1),
  displayName: z.string().default(''),
  email: z.string().default(''),
  scope: z.enum(['EUROPE', 'CHILE', 'ALL']),
  priority: z.coerce.number().int().nonnegative().default(0),
  available: z.boolean().default(false),
  active: z.boolean().default(false),
  effectiveFrom: z.string().optional(),
  effectiveUntil: z.string().optional(),
  version: z.coerce.number().int().nonnegative().default(0),
  lastModified: z.string().optional(),
  lastModifiedBy: z.string().optional(),
});

// One entry from GET /v1/fieldOpsPeople. proto3 JSON omits zero-value
// enums, so `source` / `team` are simply absent for a person tagged with
// only the other one; `.catch(undefined)` also tolerates a future enum
// value this build doesn't know yet.
export const fieldOpsPersonSchema = z.object({
  email: z.string().min(1),
  displayName: z.string().catch(''),
  source: z
    .enum(['CUSTOMER_SUCCESS', 'SALES', 'ENGINEERING', 'REMOTE_OPS', 'AD_HOC'])
    .optional()
    .catch(undefined),
  team: z.enum(['EUROPE', 'CHILE']).optional().catch(undefined),
  version: z.coerce.number().int().nonnegative().catch(0),
});

// Wire shape of FieldWorkRequestsService.GetFieldWorkStats. protojson
// serves enums as name strings, int/double as number|string, and omits
// zero/empty fields -- hence the coercions, defaults and .catch fallbacks.
const durationsWireSchema = z
  .object({
    count: z.coerce.number().catch(0),
    avgHours: z.coerce.number().catch(0),
    medianHours: z.coerce.number().catch(0),
  })
  .catch(() => ({ count: 0, avgHours: 0, medianHours: 0 }));

export const fieldWorkStatsWireSchema = z.object({
  total: z.coerce.number().catch(0),
  open: z.coerce.number().catch(0),
  closed: z.coerce.number().catch(0),
  overdue: z.coerce.number().catch(0),
  unassignedOpen: z.coerce.number().catch(0),
  currentlyBlocked: z.coerce.number().catch(0),
  blockedTotalHours: z.coerce.number().catch(0),
  byStatus: z
    .array(z.object({ status: z.string().catch(''), count: z.coerce.number().catch(0) }))
    .catch(() => []),
  byTeam: z
    .array(
      z.object({
        team: z.string().catch(''),
        open: z.coerce.number().catch(0),
        total: z.coerce.number().catch(0),
      }),
    )
    .catch(() => []),
  bySource: z
    .array(
      z.object({
        source: z.string().catch(''),
        open: z.coerce.number().catch(0),
        total: z.coerce.number().catch(0),
      }),
    )
    .catch(() => []),
  byAssignee: z
    .array(
      z.object({
        assignee: z.string().catch(''),
        open: z.coerce.number().catch(0),
        total: z.coerce.number().catch(0),
      }),
    )
    .catch(() => []),
  leadTime: durationsWireSchema,
  cycleTime: durationsWireSchema,
  blocked: durationsWireSchema,
});

export type FieldWorkStatsWire = z.infer<typeof fieldWorkStatsWireSchema>;

export const auditEventSchema = z.object({
  auditId: z.string(),
  recordId: z.string(),
  operationType: z.string(),
  changedAt: z.string(),
  changedBy: z.string().optional(),
  old: z.record(z.string(), z.unknown()).nullish(),
  new: z.record(z.string(), z.unknown()).nullish(),
});

// ---- Fulfilment (tidaltech/koi#8084) ----
// Array fallbacks are factories: `.catch([])` would hand every parsed row
// the same array instance.
// int64/uint64 arrive as JSON strings from the grpc-json transcoder; ids the
// backend leaves empty ("") are treated as absent.

const count = z.coerce.number().int().nonnegative().catch(0);
const optionalId = z
  .string()
  .optional()
  .transform((value) => (value ? value : undefined));

export const fulfillmentRequirementSchema = z.object({
  type: z.enum(requirementTypes),
  label: optionalId,
  partIds: z.array(z.string()).catch(() => []),
  stockItemId: optionalId,
  quantity: count,
});

export const fulfillmentTemplateSchema = z.object({
  id: z.string().min(1),
  name: z.string().catch(''),
  description: optionalId,
  requirements: z.array(fulfillmentRequirementSchema).catch(() => []),
  active: z.boolean().catch(false),
  version: count,
  lastModified: z.string().optional(),
  lastModifiedBy: z.string().optional(),
});

export const fulfillmentPickSchema = z.object({
  id: z.string().min(1),
  lineId: optionalId,
  code: z.string().catch(''),
  itemId: optionalId,
  quantity: count,
  manualOverride: z.boolean().catch(false),
  overrideReason: z.enum(overrideReasons).optional().catch(undefined),
  overrideNote: optionalId,
  pickedBy: z.string().optional(),
  pickedAt: z.string().optional(),
  removed: z.boolean().catch(false),
  removedBy: z.string().optional(),
  removedAt: z.string().optional(),
  removeReason: optionalId,
});

export const fulfillmentOrderSchema = z.object({
  id: z.string().min(1),
  externalReference: optionalId,
  customerId: z.string().catch(''),
  siteId: z.string().catch(''),
  fieldWorkRequestId: optionalId,
  shipmentId: optionalId,
  templateId: optionalId,
  templateVersion: z.coerce.number().int().nonnegative().optional().catch(undefined),
  state: z.enum(fulfillmentStates),
  dispatchDate: z.string().optional(),
  notes: optionalId,
  lines: z
    .array(
      z.object({
        id: z.string().min(1),
        requirement: fulfillmentRequirementSchema,
        locationId: optionalId,
        itemIds: z.array(z.string()).catch(() => []),
        pickedQuantity: count,
        allocationId: optionalId,
        waitingQuantity: count,
      }),
    )
    .catch(() => []),
  picks: z.array(fulfillmentPickSchema).catch(() => []),
  amendments: z
    .array(
      z.object({
        lineId: optionalId,
        action: z.enum(['REDUCE_QUANTITY', 'SUBSTITUTE', 'CANCEL_LINE']),
        before: fulfillmentRequirementSchema.optional().catch(undefined),
        after: fulfillmentRequirementSchema.optional().catch(undefined),
        reason: optionalId,
        amendedBy: z.string().optional(),
        amendedAt: z.string().optional(),
      }),
    )
    .catch(() => []),
  cancelReason: optionalId,
  cancelledBy: z.string().optional(),
  cancelledAt: z.string().optional(),
  releasedBy: z.string().optional(),
  releasedAt: z.string().optional(),
  recipient: optionalId,
  handoverMethod: z.enum(handoverMethods).optional().catch(undefined),
  handedOverBy: z.string().optional(),
  handedOverAt: z.string().optional(),
  createdBy: z.string().optional(),
  createTime: z.string().optional(),
  version: count,
  lastModified: z.string().optional(),
  lastModifiedBy: z.string().optional(),
});

export const stockItemSchema = z.object({
  id: z.string().min(1),
  sku: z.string().catch(''),
  displayName: z.string().catch(''),
  description: optionalId,
  stockUnit: z.enum(stockUnits).catch('PIECE'),
  active: z.boolean().catch(false),
  barcodes: z.array(z.string()).catch(() => []),
  // STOCK_USAGE_UNSPECIFIED (or anything unknown) reads as unset.
  usage: z.enum(stockUsages).optional().catch(undefined),
  minimumQuantity: count,
  imageId: optionalId,
  version: count,
});

export const warehouseLocationSchema = z.object({
  id: z.string().min(1),
  siteId: z.string().catch(''),
  parentId: optionalId,
  type: z.string().catch(''),
  code: z.string().catch(''),
  displayName: optionalId,
  active: z.boolean().catch(false),
  version: count,
});

export const stockBalanceSchema = z.object({
  stockItemId: z.string().min(1),
  locationId: z.string().min(1),
  onHand: z.coerce.number().int().catch(0),
  allocated: z.coerce.number().int().catch(0),
  available: z.coerce.number().int().catch(0),
});

export const stockMovementSchema = z.object({
  id: z.string().min(1),
  stockItemId: z.string().catch(''),
  quantity: z.coerce.number().int().nonnegative().catch(0),
  fromLocationId: optionalId,
  toLocationId: optionalId,
  reason: z.enum(movementReasons),
  referenceType: optionalId,
  referenceId: optionalId,
  note: optionalId,
  actor: z.string().optional(),
  createTime: z.string().optional(),
});

// Goods Intake (tidaltech/koi#8100). int64 quantities arrive as strings;
// an uncounted line has no receivedQuantity, which must stay undefined.
const quantity = z.coerce.number().int().nonnegative();

export const expectedReceiptLineSchema = z.object({
  id: z.string().min(1),
  stockItemId: z.string().catch(''),
  expectedQuantity: quantity.catch(0),
  receivedQuantity: quantity.optional().catch(undefined),
  damagedQuantity: quantity.optional().catch(undefined),
  locationId: optionalId,
  note: optionalId,
  movementId: optionalId,
});

export const receiptDiscrepancySchema = z.object({
  lineId: optionalId,
  stockItemId: z.string().catch(''),
  kind: z.enum(discrepancyKinds),
  quantity: quantity.catch(0),
  note: optionalId,
});

export const expectedReceiptSchema = z.object({
  id: z.string().min(1),
  siteId: z.string().catch(''),
  supplier: optionalId,
  supplierReference: optionalId,
  identifiers: z.array(z.string()).catch(() => []),
  source: z.enum(receiptSources).catch('MANUAL'),
  expectedAt: z.string().optional(),
  notes: optionalId,
  lines: z.array(expectedReceiptLineSchema).catch(() => []),
  state: z.enum(receiptStates),
  discrepancies: z.array(receiptDiscrepancySchema).catch(() => []),
  cancelReason: optionalId,
  postedBy: optionalId,
  postedAt: z.string().optional(),
  createdBy: optionalId,
  createTime: z.string().optional(),
  version: count,
  lastModified: z.string().optional(),
});

export const sitePenCountsSchema = z.object({
  siteId: z.string().min(1),
  date: z.string().catch(''),
  pens: z.number().int().nonnegative().catch(0),
  pensWithFish: z.number().int().nonnegative().catch(0),
  pensWithData: z.number().int().nonnegative().catch(0),
});

export const siteNoteSchema = z.object({
  id: z.string().min(1),
  customerId: z.string().catch(''),
  siteId: z.string().catch(''),
  kind: z.enum(siteNoteKinds).catch('NOTE'),
  text: z.string().catch(''),
  author: z.string().optional(),
  createTime: z.string().optional(),
});

// Contacts (tidaltech/koi#8359). proto3 JSON omits defaults, so lists and
// flags may be absent; an unknown purpose from a newer server is dropped.
const contactPurposeSchema = z.enum(contactPurposes);
export const contactScopeSchema = z.object({
  level: z.enum(contactLevels),
  siteId: z.string().optional(),
  country: z.string().optional(),
  region: z.string().optional(),
  purposes: z
    .array(z.unknown())
    .default([])
    .transform((values) =>
      values.flatMap((value) => {
        const parsed = contactPurposeSchema.safeParse(value);
        return parsed.success ? [parsed.data] : [];
      }),
    ),
  primary: z.boolean().optional(),
});

export const contactSchema = z.object({
  id: z.string().min(1),
  customerId: z.string().optional(),
  accountId: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  title: z.string().optional(),
  role: z.string().optional(),
  userId: z.string().optional(),
  language: z.string().optional(),
  notes: z.string().optional(),
  inactive: z.boolean().optional(),
  address: z
    .object({
      line1: z.string().optional(),
      line2: z.string().optional(),
      postcode: z.string().optional(),
      city: z.string().optional(),
    })
    .optional(),
  scopes: z.array(contactScopeSchema).default([]),
  version: z.coerce.number().int().nonnegative().default(0),
  lastModified: z.string().optional(),
  lastModifiedBy: z.string().optional(),
});

export const contactResolutionSchema = z.object({
  purpose: contactPurposeSchema,
  level: z
    .enum(['LEVEL_UNSPECIFIED', ...contactLevels])
    .optional()
    .transform((level) => (level === 'LEVEL_UNSPECIFIED' ? undefined : level)),
  contacts: z.array(contactSchema).default([]),
});

// Notifications (tidaltech/koi#8361). uint32/uint64 arrive as numbers or
// strings; enums a newer server adds are dropped to undefined.
const noticeCount = z.coerce.number().int().nonnegative().default(0);
const optionalDays = z.coerce.number().int().nonnegative().optional();

export const notificationRunSchema = z.object({
  id: z.string().min(1),
  createTime: z.string().optional(),
  startedBy: z.string().optional(),
  watchOnly: z.boolean().default(false),
  devices: noticeCount,
  included: noticeCount,
  skipped: noticeCount,
  notDue: noticeCount,
  emailsSent: noticeCount,
  emailsFailed: noticeCount,
});

export const notificationEmailSchema = z.object({
  id: z.string().min(1),
  runId: z.string().optional(),
  recipient: z.string().default(''),
  subject: z.string().optional(),
  noticeIds: z.array(z.string()).default([]),
  status: z.enum(emailStatuses).optional().catch(undefined),
  error: z.string().optional(),
  createTime: z.string().optional(),
});

export const penActivitySchema = z.object({
  deviceId: z.string().default(''),
  siteId: z.string().default(''),
  pen: z.string().default(''),
  firstDay: z.string().default(''),
  lastDay: z.string().default(''),
  days: z.coerce.number().int().nonnegative().default(0),
});

export const deviceNoticeSchema = z.object({
  id: z.string().min(1),
  runId: z.string().optional(),
  itemId: z.string().default(''),
  customerId: z.string().optional(),
  siteId: z.string().optional(),
  situation: z.enum(deviceSituations).optional().catch(undefined),
  message: z.enum(deviceMessages).optional().catch(undefined),
  decision: z.enum(noticeDecisions).optional().catch(undefined),
  skipReason: z.string().optional(),
  waitingSince: z.string().optional(),
  daysWaiting: noticeCount,
  lastHeartbeatSiteId: z.string().optional(),
  penId: z.string().optional(),
  firstIncluded: z.string().optional(),
  escalationLevel: noticeCount,
  recipients: z.array(z.string()).default([]),
  customerRecipients: z.array(z.string()).default([]),
  language: z.string().optional(),
  createTime: z.string().optional(),
  deviceType: z.string().optional(),
  replacedByItemId: z.string().optional(),
});

export const notificationSettingsSchema = z.object({
  id: z.string().optional(),
  customerId: z.string().optional(),
  quietAfterDays: optionalDays,
  neverInstalledAfterDays: optionalDays,
  reminderAfterDays: z.array(z.coerce.number().int().nonnegative()).default([]),
  escalateAfterDays: optionalDays,
  digestIntervalDays: optionalDays,
  snoozeMaxDays: optionalDays,
  signalWindowDays: optionalDays,
  watchOnlyUntil: z.string().optional(),
  delivery: z.enum(deliveries).optional().catch(undefined),
  version: noticeCount,
  lastModified: z.string().optional(),
  lastModifiedBy: z.string().optional(),
});

export const notificationSnoozeSchema = z.object({
  id: z.string().min(1),
  itemId: z.string().default(''),
  customerId: z.string().optional(),
  siteId: z.string().optional(),
  until: z.string().default(''),
  reason: z.string().default(''),
  createdBy: z.string().optional(),
  createTime: z.string().optional(),
});
